import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { DefaultRiskManagementType } from "../models/chart-style/chart-settings-popup.model";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { QuoteType, TradeModel, TradeOrderType } from "../models/trade/trade.model";
import { IComponent } from "./component.interface";
import { DrawExitOrderComponent } from "./draw-exit-order.component";
import { InformationPopupComponent } from "./information-popup.component";
import { LastPriceComponent } from "./last-price.component";
import { ProfitTargetPlotLine } from "./profit-target-plot-line.component";
import { StopLossPlotLine } from "./stop-loss-plot-line.component";
import { TradeComponent } from "./trade.component";

export class ExitOrderPlotLineComponent implements IComponent {
    public id = '';
    public mouseHover = false;
    public elementWidth: number | null = null;
    public elementHeight: number | null = null;

    public readonly EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS = 54;
    public readonly EXTRA_RIGHT = 85;
    public readonly EXTRA_PADDING_RIGHT = -20;
    private base: IChartContainer;
    private tradeComponent: TradeComponent;
    public profitTargetPlotLine: ProfitTargetPlotLine | null = null;
    public stopLossPlotLines: Array<StopLossPlotLine> = [];
    public drawExitOrderComponent: DrawExitOrderComponent;
    public position: CurrentPositionViewModel;
    public model: TradeOrderDataModel | null = null;
    private color = new ChartStyleColorModel({ r: 0, g: 79, b: 208 });
    public lastPrice = 0;
    public isLong = false;
    public hide = false;
    private plotLine: any = null;
    private clonedPlotLine: any = null;
    private g: any = null;
    private bracketIcon: any = null;
    private fObj: any = null;
    private index = 1;
    public left = -1;
    public top = -1;
    private divElement: HTMLElement | null = null;
    private lastPriceDivElement: HTMLElement | null = null;
    private guid = StringHelpers.newGuid();
    private removed = false;
    public disabled = false;
    
    constructor(base: IChartContainer, drawExitOrderComponent: DrawExitOrderComponent, position: CurrentPositionViewModel, model: TradeOrderDataModel) {
        this.base = base;
        this.drawExitOrderComponent = drawExitOrderComponent;
        this.position = position;
        this.model = model;
        this.id = 'exit-order-' + this.position.getKey() + '-' + this.guid;
        this.lastPrice = base.tiChartSeries.lastPrice;
        this.tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;

        this.validateIsLongOrShort();
        this.createSytle();
        this.setPosition();
        this.setElementWidthAndHeight();
        this.init();
    }

    public onExtreamChange() {
        if (this.removed) return;
        this.createSytle();
        this.setPosition();

        this.profitTargetPlotLine?.onExtreamChange();
        this.stopLossPlotLines?.forEach(x => x?.onExtreamChange());
    }

    public onRedraw() {
        if (this.removed) return;
        this.createSytle();
        this.setPosition();

        this.profitTargetPlotLine?.onRedraw();
        this.stopLossPlotLines?.forEach(x => x?.onRedraw());
    }

    public onLastPriceChange(lastPrice: number) {
        if (this.removed) return;
        this.lastPrice = lastPrice;
        this.createSytle();
        this.setPosition();
    }

    public disable() {
        this.disabled = true;
        this.divElement?.setAttribute('style', 'opacity: 0.7 !important; cursor: default !important;');
    }

    public anyPlotLinesMoving(): boolean {
        const plotLineMoving = this.profitTargetPlotLine?.moveMode ?? false;
        const stopLossesMoving =  this.stopLossPlotLines.some(x => x?.moveMode ?? false);
        return plotLineMoving || stopLossesMoving;
    }

    private setElementWidthAndHeight() {
        const element = this.getExitOrderWrapperElement();
        element.setAttribute('style', 'opacity: 0 !important; visibility: hidden !important; position: absolute !important; width: auto !important');
        this.base.tiChartSeries?.element?.insertAdjacentElement('beforeend', element);
        const width = element.clientWidth;
        const height = element.clientHeight;
        this.elementWidth = Math.ceil(width + this.EXTRA_WIDTH_FOR_PROFIT_STOP_ICONS);
        this.elementHeight = Math.ceil(height);
        element.remove();
    }

    public remove(force?: boolean) {
        this.removed = true;

        this.base.tiChartSeries.preventDragging = false;
        this.removeProfitTarget();
        this.removeStopLosses();

        if (!this.disabled || force) {
            this.divElement?.remove();
            this.divElement = null;

            const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
            if (!yAxis) {
                return;
            }

            yAxis.removePlotLine(this.id);
            yAxis.removePlotLine(this.id + '-cloned');

            this.base.tiChartSeries.removeSubscriberByKey(this.id);
            this.base.domEvents.removeSubscribersByKey(this.id);

            this.removeStyle();
            try {
                this.lastPriceDivElement?.remove();
                this.fObj?.destroy();
                this.g?.destroy();
                this.bracketIcon?.destroy();

                this.drawExitOrderComponent.exitOrderPlotLine = null;
            }
            catch { }
        }
    }

    public createProfitTargetFromTradeOrder(model: TradeOrderDataModel) {
        this.removeProfitTarget();

        if (this.removed) return;
        // if (model.currentShares != this.position.shares) {
        //     this.base.chartService.requestUpdateChartTrade(model.id, model.price, this.position.shares);
        // }

        this.profitTargetPlotLine = new ProfitTargetPlotLine(this.base, this, this.left, model);
        this.divElement?.querySelector('.profit-target-button')?.classList.add('hidden');
    }

    public createStopLossFromTradeOrder(model: TradeOrderDataModel) {
        this.removeStopLosses();

        if (this.removed) return;
        // if (model.currentShares != this.position.shares) {
        //     this.base.chartService.requestUpdateChartTrade(model.id, model.price, this.position.shares);
        // }

        const stopLossPlotLine = new StopLossPlotLine(this.base, this, this.left, model, false);
        this.stopLossPlotLines.push(stopLossPlotLine);
        this.divElement?.querySelector('.stop-loss-button')?.classList.add('hidden');
    }

    public createTemporaryStopLoss(model: Array<TradeOrderDataModel>) {
        this.removeStopLosses();

        if (this.removed) return;
        for (const m of model){
            const diff = this.position.averageCost - m.price;
            m.temporaryStopLoss.zIndex = Math.abs(diff);
        }

        model.sort((a, b) => {
            return a.temporaryStopLoss.zIndex - b.temporaryStopLoss.zIndex;
        });

        let zIndex = 5;
        for (const m of model) {
            m.temporaryStopLoss.zIndex = zIndex;
            const stopLossPlotLine = new StopLossPlotLine(this.base, this, this.left, m, true);
            this.stopLossPlotLines.push(stopLossPlotLine);
            zIndex--;
        }

        this.divElement?.querySelector('.stop-loss-button')?.classList.add('hidden');
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();

        // const color = !this.hide ? this.color.toRGB() : 'transparent';
        const color = 'transparent';
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke: ${color} !important;}`;
        styleTag += `.highcharts-plot-line.${className}-cloned {stroke: transparent !important; stroke-width: 18px !important;}`;

        styleTag += `
            .last-price-wrapper.exit-order {
                background-color: ${this.color.toRGB()} !important;
            } 
            .last-price-wrapper.exit-order .line {
                position: absolute;
                right: 0px;
                top: 50%;
                background-color: ${this.color.toRGB()} !important;
                height: 1px;
            }
            .last-price-wrapper.exit-order .last-price p {
                z-index: 1;
                position: relative;
            }
            .last-price-wrapper.exit-order .last-price:before {
                border-color: transparent ${this.color.toRGB()} transparent transparent !important;
            }
        `;
        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];

        this.createSytle();
        if (yAxis) {
            this.plotLine = yAxis.addPlotLine({
                value: this.position.averageCost,
                className: this.id,
                id: this.id,
                zIndex: this.index,
            });

            this.clonedPlotLine = yAxis.addPlotLine({
                value: this.position.averageCost,
                className: this.id + '-cloned',
                id: this.id + '-cloned',
                zIndex: this.index + 3,
                events: {
                    mouseover: () => {
                        clearTimeout(this.mouseHoverTimer);
                        this.mouseHoverTimer = setTimeout(() => {
                            const element = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);

                            this.toFront();

                            this.mouseHover = true;
                            this.base.tiChartSeries.preventDragging = true;
                            element?.addClass('hover');
                        }, 150);
                    },
                    mouseout: () => {
                        this.mouseHover = false;
                        clearTimeout(this.mouseHoverTimer);
                        const element = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);

                        this.toNormal();
                        element?.removeClass('hover');
                    },
                }
            });

            this.createIcon();

            setTimeout(() => {
                this.setPosition();
            }, 500);
        }
    }

    public toFront() {
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 17 });
        }
    }

    public toNormal() {
        if (!!this.g?.element) {
            this.g?.attr({ zIndex: 16 });
        }
    }

    public getColor(positive: boolean): ChartStyleColorModel {
        if (positive) {
            return new ChartStyleColorModel({
                r: 38,
                g: 142,
                b: 55
            });
        } else {
            return new ChartStyleColorModel({
                r: 240,
                g: 54,
                b: 68
            });
        }
    }

    private validateIsLongOrShort() {
        if (this.position.shares >= 0) {
            this.isLong = true;
        }
    }

    private setInitialTop() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        this.top = yAxis.toPixels(this.position.averageCost, true);

        if (this.base.marketingWebChartMode) {
            this.top += 45;
        } else {
            this.top += 33;
        }
    }

    private setPosition() {
        const container = this.base.chartWrapper;
        if (!container || this.removed) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const shouldDisplayExitOrder = this.tradeComponent && this.lastPrice != 0;
        if (shouldDisplayExitOrder) {
            const profit = this.position.getTotalProfit(this.lastPrice);
            const profitString = FormatHelpers.numberToStringThousandSeparators(Math.abs(profit), true, 0);

            this.setInitialTop();
            this.hide = false;
            if (this.position.averageCost > yAxis.max || this.position.averageCost < yAxis.min) {
                this.hide = true;
            }

            container.querySelectorAll<HTMLElement>(`.exit-order-wrapper.${this.id}`).forEach((el: HTMLElement) => {
                if (!el.classList.contains('cloned')) {
                    if (this.hide) {
                        el.style.display = 'none';
                    } else {
                        el.style.display = '';
                    }

                    el.findFirst('.center')?.setInnerHtml(`
                    <p class="value ${profit >= 0 || profitString == '0' ? '' : 'neg'}">
                        ${profit >= 0 || profitString == '0' ? '' : '-'}$${profitString}
                    </p>`);
                }
            });

            this.setLineRight(`.exit-order-wrapper.${this.id}`, !!this.bracketIcon, this.model?.entryTime);
            try {
                this.fObj?.element?.setAttribute('y', this.top);
            } catch { }
        }

        this.createLastPrice();
    }
    
    public getOrderLineWidth(entryTime: Date | null): number {
        let xValue: number | null = null;
        let width = this.base.tiChartSeries.element.clientWidth;
        if (entryTime) {
            xValue = this.base.tiChartSeries.getXValueByDateTime(entryTime);
        }

        const mainChartSerie: any = this.base.tiChartSeries.highStockChart?.series?.[0]
        const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0]

        if (mainChartSerie?.processedXData?.length && xValue) {
            if (mainChartSerie && xAxis && mainChartSerie.processedXData.some((x: number) => x == xValue)) {
                const currentXValueWidth = xAxis.toPixels(xValue) ?? 0;

                let widthToReduce = 35;
                const chartExtraSmall = this.base.isExtraSmall();
                if (this.base.tiChartSeries?.chartStyleSettings.showSmartRiskLevel && !chartExtraSmall) {
                    widthToReduce += 100;
                } else {
                    widthToReduce += 70;
                }
                
                width = Math.abs(this.base.tiChartSeries.element.clientWidth - currentXValueWidth - widthToReduce);
            } else if (mainChartSerie.processedXData[mainChartSerie.processedXData.length - 1] < xValue) {
                width = 0;
            }
        }

        return width;
    }

    private createLastPrice() {

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const currentYValue = yAxis.toPixels(this.position.averageCost) ?? 0;
        const myHeight = 7;

        const valueTop = (yAxis.toPixels(yAxis.max) ?? 0) - 16;
        const valueBottom = (yAxis.toPixels(yAxis.min) ?? 0) - 16;

        let top = currentYValue - myHeight;
        let hide = false;

        if (this.position.averageCost > yAxis.max || valueTop > top) {
            top = valueTop;
            hide = true;
        } else if (this.position.averageCost < yAxis.min || top > valueBottom) {
            top = valueBottom;
            hide = true;
        }

        const priceToString = '$' + FormatHelpers.numberToStringThousandSeparators(this.position.averageCost, true);

        if (!this.lastPriceDivElement) {
            this.lastPriceDivElement = this.base.chartWrapper.querySelector<HTMLElement>('.last-price-wrapper.exit-order');
        }

        if (this.lastPriceDivElement) {
            this.lastPriceDivElement.style.top = top + 'px';

            const pTag = this.lastPriceDivElement.querySelector('p');
            if (pTag) {
                pTag.innerHTML = priceToString;
            }
        } else {
            this.lastPriceDivElement = document.createElement('div');
            this.lastPriceDivElement.className = 'last-price-wrapper exit-order';
            this.lastPriceDivElement.style.top = top + 'px';
            this.lastPriceDivElement.innerHTML = `
                <div class="last-price">
                    <p>${priceToString}</p>
                    <div class="line"></div>
                </div>
            `;

            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.lastPriceDivElement);
            }
        }

        if (hide) {
            this.lastPriceDivElement?.classList.add('hidden');
        } else {
            this.lastPriceDivElement?.classList.remove('hidden');
        }
    }

    private getExitOrderWrapperElement(): HTMLElement {
        const profit = this.position.getTotalProfit(this.lastPrice);

        const lineWidth = this.getOrderLineWidth(this.model?.entryTime);
        const color = this.color.toRGB();

        const profitVal = Math.abs(profit);
        // const profitString = FormatHelpers.numberToStringThousandSeparators(profitVal, false, profitVal < 1 ? 2 : 0);
        const profitString = FormatHelpers.numberToStringThousandSeparators(profitVal, true, 0);
        const divElement = document.createElement('div');
        divElement.className = `exit-order-wrapper ${this.id}`;
        let title = this.position.temporary ? '' : `Select &quot;X&quot; to Close (flatten) this Position`;
        
        divElement.setAttribute('style', `${this.hide ? `display: none;` : ``}`);
        divElement.innerHTML = `
            <span class="line" style="position: absolute; height: 1px; top: 10px;width: ${lineWidth}px; background-color: ${color};"></span>
            <div class="profit-target-button ${this.position.temporary ? 'hidden' : ''}" title="Click and Drag to add Profit Target">
                <svg style="cursor:pointer;color:${this.color.toRGB()};">
                    <use href="${this.base.basePath}assetfiles/img/profit-target-v2.svg#profit-target-v2"></use>
                </svg>
            </div>
            <div class="stop-loss-button ${this.position.temporary ? 'hidden' : ''}" title="Click and Drag to add Stop Loss">
                <svg style="cursor:pointer;color:${this.color.toRGB()};">
                    <use href="${this.base.basePath}assetfiles/img/stop-loss-v2.svg#stop-loss-v2"></use>
                </svg>
            </div>
            <div class="left item" title="${title}">
                <p class="value">${this.isLong ? '' : '-'}${Math.abs(this.position.shares)}</p>
            </div>
            <div class="center item" style="border: 1px solid ${this.color.toRGB()}; border-left: none; border-right: none;"  title="${title}">
                <p class="value ${profit >= 0 || profitString == '0' ? '' : 'neg'}">
                    ${profit >= 0 || profitString == '0' ? '' : '-'}$${profitString}
                </p>
            </div>
            <div class="right item" style="border: 1px solid ${this.color.toRGB()}; border-left: none; ${this.position.temporary ? 'cursor: default !important;' : ''}" title="${title}">
                <svg style="color:${this.color.toRGB()};" class="exit-order ${this.position.temporary ? 'hidden' : ''}" >
                    <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                </svg>
            </div>
        `;

        return divElement;
    }

    private clossAll() {
        const tradeIsBuy = !this.isLong;
        const tradeModel = new TradeModel({
            price: this.lastPrice,
            isBuy: tradeIsBuy,
            orderType: TradeOrderType.Market,
            quoteType: QuoteType.Shares,
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares)
        });

        this.mouseHover = false;
        this.base.tiChartSeries.preventDragging = false;
        if (!this.position.temporary) {
            this.base.chartService.requestManualTrade(tradeModel);
        } else {
            (this.base.chartService as any)?.clearTemporaryPosition();
        }

        this.closeProfitTarget();
        this.closeStopLoss();
        this.disable();
        this.remove();
    }

    private mouseHoverTimer: any;
    private createIcon() {
        const container = this.base.chartWrapper;
        if (!container) return;

        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) return;

        const shouldDisplayExitOrder = this.tradeComponent && this.lastPrice != 0;
        if (shouldDisplayExitOrder) {

            try {
                if (this.g) {
                    this.g?.destroy();
                }
            } catch { }

            this.setInitialTop();

            const showSmartRisk = this.base.tiChartSeries.chartStyleSettings?.showSmartRiskLevel ?? false;
            const smartRiskLeft = showSmartRisk ? 34 : (this.base.marketingWebChartMode ? 0 : 4);
            this.left = this.base.tiChartSeries.element.clientWidth - this.elementWidth - ((this.EXTRA_RIGHT + smartRiskLeft) * 1.20);

            if (isNaN(this.top) || isNaN(this.left)) return;

            const renderer = this.base.tiChartSeries.highStockChart.renderer;
            this.hide = false;
            if (this.position.averageCost > yAxis.max || this.position.averageCost < yAxis.min) {
                this.hide = true;
            }

            this.divElement = this.getExitOrderWrapperElement();
            this.g = renderer.g(this.id).attr({
                zIndex: 16
            });

            const foreignObjectElement = renderer.createElement('foreignObject');
            foreignObjectElement.element.appendChild(this.divElement);
            foreignObjectElement.element.setAttribute('width', this.elementWidth);
            foreignObjectElement.element.setAttribute('height', 25 + '');
            foreignObjectElement.element.setAttribute('overflow', 'visible');
            foreignObjectElement.element.setAttribute('x', this.left);
            foreignObjectElement.element.setAttribute('y', this.top);
            foreignObjectElement.add(this.g);

            this.fObj = foreignObjectElement;
            this.g.add();

            setTimeout(() => {
                const originalElement = container?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
                originalElement?.ifExistThen((b: HTMLElement) => {
                    if (b.containClass('inited')) {
                        return;
                    }
                    b.addClass('inited');
                    b.addEventListener('mouseenter', () => {
                        this.mouseHover = true;
                        this.base.tiChartSeries.preventDragging = true;
                        clearTimeout(this.mouseHoverTimer);

                        this.toFront();

                        b.addClass('hover');
                    });
                    b.addEventListener('click', () => {
                        this.mouseHover = true;
                        this.base.tiChartSeries.preventDragging = true;
                        clearTimeout(this.mouseHoverTimer);

                        this.toFront();

                        b.addClass('hover');
                    });
                    b.addEventListener('mouseleave', () => {
                        b.removeClass('hover');

                        this.toNormal();

                        this.mouseHoverTimer = setTimeout(() => {
                            this.mouseHover = false;
                            this.base.tiChartSeries.preventDragging = false;
                        }, 500);
                    });
                    b.findFirst('.right')?.ifExistThen((r: HTMLElement) => {
                        const remove = () => {
                            if (this.position.temporary || this.removed || this.disabled) return;
                            
                            const shouldDisplayPopup = this.base.tiChartSeries.chartStyleSettings.chartSettingsPopupModel.displayOrderConfirmationPopup;

                            const popupMessage = this.getExitOrderMessage();
                            this.showPopupOrSubmit(shouldDisplayPopup, `Exit Order`, popupMessage, () => {
                                this.clossAll();
                            });
                        }
                        if (r.containClass('inited')) {
                            return;
                        }
                        r.classList.add('inited');
                        r.addEventListener('mouseenter', () => {
                            r.classList.add('hover');
                        });
                        r.addEventListener('mouseleave', () => {
                            r.classList.remove('hover');
                        });
                        r.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            remove();
                        });

                        const s = r.querySelector('svg')
                        if (!s || s.classList.contains('inited')) {
                            return;
                        }
                        s.classList.add('inited');
                        s.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            remove();
                        });
                    });
                    b.findFirst('.profit-target-button')?.ifExistThen((p: HTMLElement) => {
                        if (p.containClass('inited') || this.position.temporary || this.removed || this.disabled) {
                            return;
                        }

                        p.addClass('inited');

                        if (this.drawExitOrderComponent.requestingProfitTarget) {
                            p?.addClass('hidden');
                        }

                        p.addEventListener('mousedown', () => {
                            this.base.tiChartSeries.preventDragging = true;
                        });
                        p.addEventListener('mouseup', () => {
                            setTimeout(() => {
                                this.base.tiChartSeries.preventDragging = false;
                            }, 500);
                        });
                        p.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!this.profitTargetPlotLine && !this.drawExitOrderComponent.requestingProfitTarget) {
                                const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
                                if (!yAxis) {
                                    return;
                                }

                                p?.addClass('hidden');

                                const price = this.isLong ? this.getPrice_LongProfitTarget() : this.getPrice_ShortProfitTarget();
                                const tradeModel = this.isLong ? this.generateOrder_LongProfitTarget(price) : this.generateOrder_ShortProfitTarget(price);
                                this.base.chartService.requestManualTrade(tradeModel);

                                this.drawExitOrderComponent.requestingProfitTarget = true;

                            }
                        });
                    });
                    b.findFirst('.stop-loss-button')?.ifExistThen((p: HTMLElement) => {
                        if (p.containClass('inited') || this.position.temporary || this.removed || this.disabled) {
                            return;
                        }

                        p.addClass('inited');

                        if (this.drawExitOrderComponent.requestingStopLoss) {
                            p?.addClass('hidden');
                        }

                        p.addEventListener('mousedown', () => {
                            this.base.tiChartSeries.preventDragging = true;
                        });
                        p.addEventListener('mouseup', () => {
                            setTimeout(() => {
                                this.base.tiChartSeries.preventDragging = false;
                            }, 500);
                        });
                        p.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!this.stopLossPlotLines.length && !this.drawExitOrderComponent.requestingStopLoss) {
                                const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
                                if (!yAxis) {
                                    return;
                                }

                                p?.addClass('hidden');

                                const price = this.isLong ? this.getPrice_LongStopLoss() : this.getPrice_ShortStopLoss();
                                const tradeModel = this.isLong ? this.generateOrder_LongStopLoss(price) : this.generateOrder_ShortStopLoss(price);

                                this.base.chartService.requestManualTrade(tradeModel);

                                this.drawExitOrderComponent.requestingStopLoss = true;
                                
                            }
                        });
                    });
                });
            }, 300);
        }
    }

    private showPopupOrSubmit(shouldDisplayPopup: boolean, popupTitle: string, popupMessage: string, submitCallback: () => void) {
        const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
        if (shouldDisplayPopup) {
            popup?.show(popupTitle, popupMessage, submitCallback);
        } else {
            submitCallback();
        }
    }

    private getPrice_LongProfitTarget(): number {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let entryPrice = this.position.averageCost;
        let lastPrice = this.lastPrice;

        const entryOrderYAxisPixels = this.top;
        const profitTargetShoudlBeAboveEntryOrder = entryPrice > lastPrice;

        const settings = this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel;
        if (settings?.useDefaultProfitTarget && settings?.defaultProfitTargetValue) {
            let price = profitTargetShoudlBeAboveEntryOrder ? entryPrice : this.lastPrice;

            if (settings.defaultProfitTargetType == DefaultRiskManagementType.Dollars) {
                price -= settings.defaultProfitTargetValue;
            } else if (settings.defaultProfitTargetType == DefaultRiskManagementType.Percentage) {
                price -= price * (settings.defaultProfitTargetValue / 100);
            }

            price = this.getRoundedPrice(price);
            return price;
        }

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        let lastPriceYAxisPixels = 0;
        if (lastPriceComp) {
            lastPriceYAxisPixels = lastPriceComp.getElementTop();
        }

        let top = profitTargetShoudlBeAboveEntryOrder ? entryOrderYAxisPixels : lastPriceYAxisPixels;
    
        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top -= 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top -= 30;
        }

        let price = this.getRoundedPrice(yAxis.toValue(top));
        return price;
    }

    private getPrice_ShortProfitTarget(): number {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        let entryPrice = this.position.averageCost;
        let lastPrice = this.lastPrice;

        const entryOrderYAxisPixels = this.top;
        const profitTargetShoudlBeBelowEntryOrder = entryPrice < lastPrice;

        const settings = this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel;
        if (settings?.useDefaultProfitTarget && settings?.defaultProfitTargetValue) {
            let price = profitTargetShoudlBeBelowEntryOrder ? entryPrice : this.lastPrice;

            if (settings.defaultProfitTargetType == DefaultRiskManagementType.Dollars) {
                price += settings.defaultProfitTargetValue;
            } else if (settings.defaultProfitTargetType == DefaultRiskManagementType.Percentage) {
                price += price * (settings.defaultProfitTargetValue / 100);
            }

            price = this.getRoundedPrice(price);
            return price;
        }

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        let lastPriceYAxisPixels = 0;
        if (lastPriceComp) {
            lastPriceYAxisPixels = lastPriceComp.getElementTop();
        }

        let top = profitTargetShoudlBeBelowEntryOrder ? entryOrderYAxisPixels : lastPriceYAxisPixels;

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top += 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top += 30;
        }

        let price = this.getRoundedPrice(yAxis.toValue(top));
        return price;
    }

    private getPrice_LongStopLoss(): number {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        const settings = this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel;
        if (settings?.useDefaultStopLoss && settings?.defaultStopLossValue) {
            let price = this.lastPrice;
            if (settings.defaultStopLossType == DefaultRiskManagementType.Dollars) {
                price += settings.defaultStopLossValue;
            } else if (settings.defaultStopLossType == DefaultRiskManagementType.Percentage) {
                price += price * (settings.defaultStopLossValue / 100);
            }

            price = this.getRoundedPrice(price);
            return price;
        }

        let top = this.top;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        if (lastPriceComp) {
            top = lastPriceComp.getElementTop();
        }

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top += 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top += 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top += 30;
        }


        let price = this.getRoundedPrice(yAxis.toValue(top));
        return price;
    }

    private getPrice_ShortStopLoss(): number {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }

        const settings = this.base.tiChartSeries.chartStyleSettings?.chartSettingsPopupModel;
        if (settings?.useDefaultStopLoss && settings?.defaultStopLossValue) {
            let price = this.lastPrice;
            if (settings.defaultStopLossType == DefaultRiskManagementType.Dollars) {
                price -= settings.defaultStopLossValue;
            } else if (settings.defaultStopLossType == DefaultRiskManagementType.Percentage) {
                price -= price * (settings.defaultStopLossValue / 100);
            }

            price = this.getRoundedPrice(price);
            return price;
        }

        let top = this.top;

        const lastPriceComp = this.base.getComponentsByType(LastPriceComponent)?.[0] as LastPriceComponent;
        if (lastPriceComp) {
            top = lastPriceComp.getElementTop();
        }

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            top -= 60;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Week) {
            top -= 30;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.Month) {
            top -= 30;
        }

        let price = this.getRoundedPrice(yAxis.toValue(top));
        return price;
    }

    public getRoundedPrice(value: number) {
        let price = value;
        if (price < 1) {
            price = parseFloat(price.toFixed(4));
        } else {
            price = parseFloat(price.toFixed(2));
        }

        return price;
    }

    private getOCAGroup(): string {
        let oca = '';
        if (this.profitTargetPlotLine) {
            oca = this.profitTargetPlotLine.model?.ocaGroup;
        } else if (this.stopLossPlotLines) {
            const sl = this.stopLossPlotLines.find(x => !x.model.isTemporary);
            oca = sl?.model?.ocaGroup;
        }

        if (oca) return oca;

        return `${this.position.symbol}-OCA-${DateHelpers.toTimeT(new Date())}`;
    }

    private generateOrder_LongProfitTarget(price: number): TradeModel {
        //Long - Profit Target is Sell Limit Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 13
        });

        return tradeModel;
    }

    private generateOrder_LongStopLoss(price: number): TradeModel {
        //Long - Stop Loss is Sell Stop Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: false,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 12
        });

        return tradeModel;
    }

    private generateOrder_ShortProfitTarget(price: number): TradeModel {
        //Short - Profit target is Buy Limit Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Limit,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 13
        });

        return tradeModel;
    }

    private generateOrder_ShortStopLoss(price: number): TradeModel {
        //Short - Stop loss is Buy Stop Order
        const tradeModel = new TradeModel({
            price: price,
            isBuy: true,
            orderType: TradeOrderType.Stop,
            quoteType: QuoteType.Shares,
            ocaGroup: this.getOCAGroup(),
            account: this.position.accountName,
            quoteQuantity: Math.abs(this.position.shares),
            source: 12
        });

        return tradeModel;
    }

    public addBracketIcon() {
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        try {
            this.bracketIcon?.destroy();
            this.bracketIcon = null;
        } catch { }

        this.setLineRight(`.exit-order-wrapper.${this.id}`, false, this.model?.entryTime);

        if (this.hide || !this.plotLine) return;

        const color = this.color;
        const renderer = this.base.tiChartSeries.highStockChart.renderer;
        const width = this.elementWidth;
        
        const EXTRA_TOP = this.base.marketingWebChartMode ? 58 : 45;
        const currentY = yAxis.toPixels(this.position.averageCost, true) + EXTRA_TOP;
        const left = this.left + width + 15;

        this.bracketIcon = renderer.circle(left, Math.floor(currentY) + 1, 3)
            .attr({
                'fill': `${color.toRGB()}`,
                'stroke': `${color.toRGB()}`,
                'stroke-width': 1.5,
                'zIndex': 12
            })
            .add();

        this.setLineRight(`.exit-order-wrapper.${this.id}`, true, this.model?.entryTime);
    }

    public setLineRight(cssSelector: string, toTheRight: boolean, entryTime?: Date) {
        const rightValue = 12;
        const linewidth = this.getOrderLineWidth(entryTime);
        this.base.chartWrapper?.querySelectorAll<HTMLElement>(cssSelector).forEach((el: HTMLElement) => {
            if (!el.classList.contains('cloned')) {
                const line = el.findFirst('.line');
                if (line) {
                    if (toTheRight) {
                        line.style.width = (linewidth + rightValue) + 'px';
                        line.style.right = `-${rightValue}px`;
                    } else {
                        line.style.width = (linewidth) + 'px';
                        line.style.right = ``;
                    }
                }
            }
        });
    }

    public closeProfitTarget() {
        if (this.profitTargetPlotLine?.tradeId) {
            this.base.chartService.requestRemoveChartTrade(this.profitTargetPlotLine?.tradeId);
            this.mouseHover = false;
            this.base.tiChartSeries.preventDragging = false;
        }

        const ocaGroup = this.profitTargetPlotLine?.model.ocaGroup ?? '';
        ocaGroup && this.drawExitOrderComponent.removeProfitTargetOcaGroups.push(ocaGroup);

        this.profitTargetPlotLine?.disable();
        // this.removeProfitTarget();
    }

    public removeProfitTarget() {
        this.profitTargetPlotLine?.remove();
        this.profitTargetPlotLine = null;
        const originalElement = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
        originalElement?.ifExistThen((b: HTMLElement) => {
            b.findFirst('.profit-target-button')?.ifExistThen((p: HTMLElement) => {
                p?.removeClass('hidden');
            });
        });
    }

    public closeStopLoss() {
        if (!this.stopLossPlotLines?.length) return;

        for (const s of this.stopLossPlotLines) {
            if (s.model.isTemporary) {
                if (s.model.temporaryStopLoss) {
                    this.base.chartService.requestRemoveTemporaryStopLoss(
                        s.model.temporaryStopLoss.accountName,
                        s.model.temporaryStopLoss.stopType,
                    );
                }
            } else {
                if (s.tradeId) {
                    this.base.chartService.requestRemoveChartTrade(s.tradeId);
                    this.mouseHover = false;
                    this.base.tiChartSeries.preventDragging = false;
                }
        
                const ocaGroup = s.model.ocaGroup ?? '';
                ocaGroup && this.drawExitOrderComponent.removeStopLossOcaGroups.push(ocaGroup);
            }
    
            s.disable();
        }
    }

    public removeStopLosses() {
        for (let s of this.stopLossPlotLines) {
            s.remove();
            s = null;
        }
        
        this.stopLossPlotLines = [];
        const originalElement = this.base.chartWrapper?.findFirst(`.exit-order-wrapper.${this.id}:not(.cloned)`);
        originalElement?.ifExistThen((b: HTMLElement) => {
            b.findFirst('.stop-loss-button')?.ifExistThen((p: HTMLElement) => {
                p?.removeClass('hidden');
            });
        });
    }

    public getExitOrderMessage(): string {
        let html = ``;
        if (this.isLong) {
            html = `<span class="b red">Sell</span> <span class="b">${Math.abs(this.position.shares)} Shares</span> of <span class="b">${this.position.symbol}</span> at <span class="b">Market,</span><br/>to Close the Position in <span class="b">${this.position.accountName}</span>?`;
        } else {
            html = `<span class="b green">Buy</span> <span class="b">${Math.abs(this.position.shares)} Shares</span> of <span class="b">${this.position.symbol}</span> at <span class="b">Market,</span><br/>to Close the Position in <span class="b">${this.position.accountName}</span>?`;
        }

        return `<span class="trade-message">${html}</span>`;
    }
}